<template>
    <div class="qn-upload">
        <el-upload ref="upload" v-bind="$attrs" :action="action" :accept="accept" :auto-upload="autoUpload"
            :max-length="maxLength" :on-change="handleChange" :on-preview="handlePreview" :on-remove="handleRemove" :on-success="handleSuccess"
            :before-upload="handleBeforeUpload" :http-request="handleUpload" :list-type="listType"
            :file-list="fileList">
            <template v-for="(index, name) in $slots" :slot="name">
                <slot :name="name" />
            </template>
            <template v-if="listType === 'picture-card'">
                <i v-if="!$slots.default" class="el-icon-plus"></i>
                <div v-if="!$slots.tip" slot="tip" class="el-upload__tip">{{ tip }}</div>
            </template>
        </el-upload>
        <preview :visible.sync="dialogs.preview" :data="preview.data" :is-edit="isCropper" :list="preview.list" @edit="handleToEdit" />
        <cropper v-if="isCropper" :visible.sync="dialogs.cropper" :data="cropper.data" :resolution="cropper.resolution" @success="handleCropper"/>
    </div>
</template>

<script>
import MediaInfoFactory from 'mediainfo.js'
import ElUpload from './upload'
import { CommonUpload } from '@/utils/HWupload'
import { calculateMd5ByChunkSize } from '@/utils'
import preview from './preview'
import cropper from './cropper'


const uploadModule = {
    PROJECTOR_PAGE: {
        platform: 'PT_MIX_BZ',
        base_module: 'PROJECTOR_PAGE',
        module: 'PROJECTOR_PAGE',
        func: 'page_img'
    },
    INTERFACE_VIDEO: {
        platform: 'PT_MIX_BZ',
        base_module: 'INTERFACE',
        module: 'HW',
        func: 'video'
    },
    INTERFACE_IMG: {
        platform: 'PT_MIX_BZ',
        base_module: 'INTERFACE',
        module: 'INTERFACE',
        func: 'img'
    }
}

// 文件比特转化为带单位大小
function bytesToSize(filesize) {
    if (!filesize) return '0 Bytes'
    const unitArr = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    let index = 0
    const srcsize = parseFloat(filesize)
    index = Math.floor(Math.log(srcsize) / Math.log(1024))
    let size = srcsize / Math.pow(1024, index)
    size = size.toFixed(2)
    return size + unitArr[index]
}

// 文件带单位大小转换为比特
function sizeToBytes(filesize) {
    if (!filesize) return 0
    filesize = filesize.toLowerCase()
    const unitArr = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    let index = 0
    index = unitArr.findIndex(unit => {
        return filesize.includes(unit.toLowerCase())
    })
    let size = parseFloat(filesize)
    size = size * Math.pow(1024, index)
    return size
}

export default {
    name: 'QnUpload',
    components: {
        ElUpload,
        preview,
        cropper
    },
    model: {
        prop: 'fileList',
        event: 'change'
    },
    props: {
        type: {
            type: String,
            default: 'PROJECTOR_PAGE'
        },
        size: {
            type: [Number, String, Array],
            default: '2MB'
        },
        accept: {
            type: String,
            default: 'image/jpg,image/png,image/jpeg'
        },
        resolution: {
            type: [String, Array],
            default: ''
        },
        frameRate: {
            type: [Number, String, Array],
            default: ''
        },
        maxLength: {
            type: [Number, String],
            default: ''
        },
        edit: {
            type: Boolean,
            default: true
        },
        listType: {
            type: String,
            default: 'picture-card'
        },
        autoUpload: {
            type: Boolean,
            default: false
        },
        fileList: {
            type: Array,
            default() {
                return []
            }
        },
        isCropper: {
            type: Boolean,
            default: true
        },
        // 严格模式，即上传的文件必须符合要求，否则不上传
        strict: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            action: 'cdnq001.up17.v1.wcsapi.com',
            headers: {},
            data: {},
            name: '',
            fileInfo: '',
            preview: {
                data: {},
                list: []
            },
            cropper: {
                resolution: '',
                data: {}
            },
            dialogs: {
                preview: false,
                cropper: false
            }
        }
    },
    computed: {
        tip() {
            let tip = '注：'
            tip += `格式${this.accept.replaceAll(/(image\/|video\/)/g, '').replaceAll(',', ' | ')}，大小`
            if (typeof this.size === 'number') {
                tip += `<=${bytesToSize(this.size)}`
            } else if (typeof this.size === 'string') {
                tip += `<=${bytesToSize(sizeToBytes(this.size))}`
            } else if (this.size instanceof Array) {
                const [min, max] = this.size
                tip += `=>${bytesToSize(sizeToBytes(min))}${max ? '且<=' + bytesToSize(sizeToBytes(max)) : ''}`
            }
            if (this.resolution) {
                tip += `，尺寸`
                if (typeof this.resolution === 'string') {
                    tip += `${this.resolution}`
                } else if (this.resolution instanceof Array) {
                    tip += `${this.resolution.join(' | ')}`
                }
            }
            if (this.frameRate) {
                tip += `，帧率`
                if (typeof this.frameRate === 'number' || typeof this.frameRate === 'string') {
                    tip += `<=${this.frameRate}`
                } else if (this.frameRate instanceof Array) {
                    const [min, max] = this.frameRate
                    tip += `>=${min}${max ? '且<=' + max : ''}`
                }
            }
            if (this.maxLength) {
                tip += `，数量<=${this.maxLength}`
            }
            return tip
        }
    },
    watch: {
        resolution: {
            handler(val) {
                if (typeof val === 'string') {
                    this.cropper.resolution = val
                } else if (val instanceof Array) {
                    this.cropper.resolution = ''
                }
            },
            immediate: true
        }
    },
    methods: {
        submit() {
            return new Promise((resolve, reject) => {
                // 有不符合要求的文件
                if (this.strict && this.fileList.some(file => file.status === 'warning')) {
                    this.$message.warning('文件不符合要求，请检查文件格式')
                    reject()
                    // 文件全部上传成功
                } else if (this.fileList.every(file => file.status === 'success')) {
                    resolve()
                    // 监听文件状态变化
                } else {
                    const cb = fileList => {
                        // 文件全部上传成功
                        if (fileList.every(file => file.status === 'success')) {
                            this.$off('change', cb) // 关闭监听
                            resolve()
                        // 有文件上传失败
                        } else if (
                            fileList.every(file => (file.status !== 'uploading') || (file.status !== 'ready'))
                            && fileList.some(file => file.status === 'fail')
                        ) {
                            this.$message.error('文件上传失败，请重新提交')
                            this.$off('change', cb) // 关闭监听
                            reject()
                        }
                    }
                    this.$on('change', cb)
                    this.$refs.upload.submit()
                }
            })
        },
        handleSuccess(res, file, fileList) {
            const i = fileList.findIndex(item => item === file)
            const url = file.response.fileUrl
            const newFile = {
                name: url.slice(url.lastIndexOf('/') + 1),
                url,
                info: file.info,
                status: file.status
            }
            fileList.splice(i, 1, newFile)
        },
        handleChange(file, fileList) {
            if (file.status === 'ready') {
                this.getFileInfo(file.raw)
                    .then(info => {
                        file.info = info
                        const checkRes = this.checkFile(file.info)
                        if (!checkRes.status) {
                            file.status = 'warning'
                            file.errMsg = `<div class="error">${checkRes.errMsg.join('<br>')}</div>`
                        }
                        this.$emit('change', fileList)
                    })
            } else {
                this.$emit('change', fileList)
            }
        },
        handlePreview(file) {
            this.preview.data = file
            this.preview.list = this.fileList
            this.dialogs.preview = true
        },
        handleRemove(file, fileList) {
            this.$emit('change', fileList)
        },
        handleBeforeUpload(file) {
            console.log('handleBeforeUpload', file)
            return true
        },
        handleUpload(options) {
            console.log('handleUpload', options)
            return new Promise((resolve, reject) => {
                const params = uploadModule[this.type]
                const rawFile = options.file
                const commonUpload = new CommonUpload(params.platform, params.module, params.func, this.getFileName(rawFile), rawFile, () => { }, params.base_module)
                // 上传完成
                commonUpload.onComplete = function (res) {
                    console.log('onComplete', res)
                    if (res.status === 200) {
                        resolve(res)
                    } else {
                        reject(res)
                    }
                }
                // 上传进度
                commonUpload.uploadProgress = (res) => {
                    const e = res.total
                    options.onProgress(e)
                }
                // 上传
                commonUpload.putFile()
            })
        },
        handleCropper(file) {
            const fileList = this.fileList
            const i = fileList.findIndex(item => item.uid === file.uid)
            fileList.splice(i, 1, file)
            this.handleChange(file, fileList)
        },
        handleToEdit(e) {
            this.cropper.data = {
                name: e.name,
                uid: e.uid,
                url: e.url
            }
            this.dialogs.cropper = true
        },
        getFileName(rawFile) {
            console.log('getFileName', rawFile)
            const suffix = `${rawFile.name.slice(rawFile.name.lastIndexOf('.'))}`
            const file = this.getFile(rawFile)
            return `${file.uid}${suffix}`
        },
        getFile(rawFile) {
            return this.$refs.upload.getFile(rawFile)
        },
        // 获取上传参数
        getParams(config, file) {
            if (!this.uploadFile) return {}
            const key = config.dir + file.name
            return {
                file: file,
                token: config.token,
                uploadUrl: 'https://' + config.request_url,
                handlers: {
                    Authorization: 'UpToken ' + config.token,
                    uploadBatch: file.id
                },
                extraConfig: {
                    bucket: config.bucket,
                    file_name_rule: config.file_name_rule,
                    max_size_mb: config.max_size_mb,
                    retryCount: 0,
                    key: key
                }
            }
        },
        // 文件检测
        checkFile(info) {
            const checkList = [
                this.checkType(info.type),
                this.checkSize(info.size),
                this.checkResolution(info.resolution)
            ]
            if (info.framRate) checkList.push(this.checkFramRate(info.framRate))
            if (checkList.every(check => check === true)) {
                return {
                    status: true
                }
            }
            return {
                status: false,
                errMsg: checkList.filter(check => check instanceof Error).map(err => err.message)
            }
        },
        // 检测类型
        checkType(type) {
            if (!this.accept) return true
            if (this.accept.includes(type)) {
                return true
            } else {
                return new Error(`当前类型：<i>${type}</i>，推荐类型：<b>${this.accept.replaceAll(',', '，')}</b>`)
            }
        },
        // 检测大小
        checkSize(size) {
            if (typeof this.size === 'number') {
                if (size <= this.size) {
                    return true
                } else {
                    return new Error(`当前大小：<i>${bytesToSize(size)}</i>，推荐大小：<b>&lt;=${bytesToSize(this.size)}</b>`)
                }
            }
            if (typeof this.size === 'string') {
                if (size <= sizeToBytes(this.size)) {
                    return true
                } else {
                    return new Error(`当前大小：<i>${bytesToSize(size)}</i>，推荐大小：<b>&lt;=${bytesToSize(sizeToBytes(this.size))}</b>`)
                }
            }
            if (this.size instanceof Array) {
                const [min, max] = this.size
                if ((min ? size >= sizeToBytes(min) : true) && (max ? size <= sizeToBytes(max) : true)) {
                    return true
                } else {
                    return new Error(`当前大小：<i>${bytesToSize(size)}</i>，推荐大小：<b>&gt;=${bytesToSize(sizeToBytes(min))}${max ? '且&lt;=' + bytesToSize(sizeToBytes(max)) : ''}</b>`)
                }
            }
        },
        // 检测分辨率
        checkResolution(resolution) {
            if (!this.resolution) return true
            if (typeof this.resolution === 'string') {
                if (resolution === this.resolution) {
                    return true
                } else {
                    return new Error(`当前分辨率：<i>${resolution}</i>，推荐分辨率：<b>${this.resolution}</b>`)
                }
            }
            if (this.resolution instanceof Array) {
                if (this.resolution.includes(resolution)) {
                    return true
                } else {
                    return new Error(`当前分辨率：<i>${resolution}</i>，推荐分辨率：<b>${this.resolution.join('，')}</b>`)
                }
            }
        },
        // 检测帧率
        checkFramRate(frameRate) {
            if (!this.frameRate) return true
            frameRate = Number(frameRate)
            if (typeof this.frameRate === 'number' || typeof this.frameRate === 'string') {
                if (frameRate <= Number(this.frameRate)) {
                    return true
                } else {
                    return new Error(`当前帧率：<i>${frameRate}</i>，推荐帧率：<b>&lt;=${this.frameRate}</b>`)
                }
            }
            if (this.frameRate instanceof Array) {
                const [min, max] = this.frameRate
                if ((min ? frameRate >= Number(min) : true) && (max ? frameRate <= Number(max) : true)) {
                    return true
                } else {
                    return new Error(`当前帧率：<i>${frameRate}</i>，推荐帧率：<b>&gt;=${min}${max ? '且&lt;=' + max : ''}</b>`)
                }
            }
        },
        // 获取文件信息
        getFileInfo(file) {
            const getSize = () => file.size
            const readChunk = (chunkSize, offset) =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.onload = (event) => {
                        if (event.target.error) {
                            reject(event.target.error)
                        }
                        resolve(new Uint8Array(event.target.result))
                    }
                    reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize))
                })
            return MediaInfoFactory()
                .then(mediaInfo => {
                    return mediaInfo.analyzeData(getSize, readChunk)
                }).then(res => {
                    res = res && res.media && res.media.track
                    const type = res[1]['@type']
                    switch (type) {
                        case 'Image':
                            return {
                                type: `${res[1]['@type'].toLowerCase()}/${res[1].Format.toLowerCase()}`,
                                size: res[0].FileSize,
                                bitDepth: res[1].BitDepth,
                                resolution: `${res[1].Width}*${res[1].Height}`
                            }
                        case 'Video':
                            return {
                                type: file.type,
                                size: res[0].FileSize,
                                bitDepth: res[1].BitDepth,
                                resolution: `${res[1].Width}*${res[1].Height}`,
                                frameRate: res[1].FrameRate
                            }
                    }
                }).then(info => {
                    return calculateMd5ByChunkSize(file)
                        .then(res => {
                            console.log('calculateMd5ByChunkSize', res)
                            info.md5 = res
                            return info
                        })
                })
        }
    }
}
</script>

<style ref="stylesheet/scss" lang="scss" scoped>
.qn-upload /deep/ {
    .el-upload-list__item.is-warning {
        border-color: #F7BA2A;
    }

    .el-upload-list__item.is-fail {
        border-color: #FF4949;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/QnUpload-1.vue?1d56f966